import _cloneDeep from 'lodash/cloneDeep';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _mergeWith from 'lodash/mergeWith';
import _pick from 'lodash/pick';
import _pickBy from 'lodash/pickBy';

import { fixDateWithTimezone, fixDateWithTimezoneParam } from 'components/MagicTimezonePicker';
import { isAssignedSlot } from 'containers/Campaign/Games/ClawMachine/constants';
import { prepareWinProbabilityForSubmit } from 'containers/Campaign/Games/winOdds';
import { getRaffleGrandPrizePositionsCount } from 'containers/Campaign/Raffle/utils';
import { tierFormToPayload } from 'containers/Campaign/Referral/TierLadder/payload';
import { TCampaignCreatePage } from 'containers/Campaign/i18n/TModules';
import { customMergeForImages, getStatic } from 'containers/Campaign/utils';
import { defaultValuesForDiff, getDiff, getFilteredDiff } from 'containers/utils';
import { doesFilterHaveValue, sanitizeFilters } from 'utils/audience';
import i18n from 'utils/i18n';

/**
 * Recursively removes image_id from image objects that have image_url.
 * This ensures predefined CDN images don't carry over image_id from previous selections.
 */
const cleanImageIds = (obj: any): any => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => cleanImageIds(item));
  }

  // Check if this is an image object with both image_url and image_id
  if (
    obj.type === 'image' &&
    obj.value &&
    typeof obj.value === 'object' &&
    obj.value.image_url &&
    obj.value.image_id
  ) {
    const { image_id, ...cleanedValue } = obj.value;
    return { ...obj, value: cleanedValue };
  }

  // Recursively clean nested objects
  const cleanedObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      // PLA-9006: prize_icons keep their image_id — it is the identity the API pairs icons on when it
      // refuses two rows carrying the same image.
      cleanedObj[key] = key === 'prize_icons' ? obj[key] : cleanImageIds(obj[key]);
    }
  }
  return cleanedObj;
};

export const getRequestConfig = ({
  toUpdate,
  baseCampaign,
  initialFormValues,
  makerCheckerCampaign,
  campaign_request_id,
  isRoleTenantAdmin,
  isImageFeatureEnabled = false,
  isMultipleRaffleTicketsEnabled = false
}) => {
  const currentFormData = prepareCampaignData(
    toUpdate,
    isImageFeatureEnabled,
    true,
    isMultipleRaffleTicketsEnabled
  );
  // Baselines skip custom-row stripping so removing/fixing a bad row is visible in the diff.
  const sanitizedBaseCampaign = prepareCampaignData(
    baseCampaign,
    false,
    false,
    isMultipleRaffleTicketsEnabled
  );
  const sanitizedInitialFormValues = prepareCampaignData(
    initialFormValues,
    false,
    false,
    isMultipleRaffleTicketsEnabled
  );

  const currentChangedValues = getDiff(sanitizedInitialFormValues, currentFormData);
  const currentChangedKeys = Object.keys(currentChangedValues);
  const previousChangedKeys = _has(currentFormData, 'changedProperties')
    ? (Array.from(currentFormData.changedProperties) as string[])
    : [];

  const validKeys = new Set([...currentChangedKeys, ...previousChangedKeys]);
  const filteredDiffData: any = getFilteredDiff(
    sanitizedBaseCampaign,
    currentFormData,
    validKeys,
    defaultValuesForDiff
  );

  // Ensure 'auto_enrol_users' and 'enrolment_type' are handled correctly
  if (
    _has(currentFormData, 'auto_enrol_users') &&
    _has(sanitizedInitialFormValues, 'auto_enrol_users') &&
    currentFormData.auto_enrol_users !== sanitizedInitialFormValues.auto_enrol_users
  ) {
    filteredDiffData.enrolment_type = currentFormData.enrolment_type;
  }

  if (_has(filteredDiffData, 'id')) {
    delete filteredDiffData.id;
  }

  if (_has(filteredDiffData, 'changedProperties')) {
    delete filteredDiffData.changedProperties;
  }

  const raffleGrandPrizesCount = getRaffleGrandPrizePositionsCount(
    currentFormData?.raffle_grand_prizes || []
  );
  const shouldClearRafflePrizeLimit =
    currentFormData?.campaign_type === 'raffle' &&
    raffleGrandPrizesCount < 2 &&
    sanitizedBaseCampaign?.prizes_per_raffle_winner === 1 &&
    currentFormData?.prizes_per_raffle_winner !== 1;

  if (shouldClearRafflePrizeLimit) {
    filteredDiffData.prizes_per_raffle_winner = null;
    currentFormData.prizes_per_raffle_winner = null;
  }

  let requestConfig = {} as {
    method: string;
    endpoint: string;
    formdata: Record<string, any>;
    successMessage: string;
  };

  switch (true) {
    /* Update new changes for existing campaign request */
    case makerCheckerCampaign && !_isEmpty(campaign_request_id) && isRoleTenantAdmin === false:
      requestConfig = {
        method: 'put',
        endpoint: `v4/dash/campaign_requests/${campaign_request_id}`,
        formdata: {
          properties: filteredDiffData
        },
        successMessage: i18n.t(`${TCampaignCreatePage}.CampaignRequestUpdated`)
      };
      break;
    /* Apply changes to the campaign for editing active campaign */
    case makerCheckerCampaign &&
      !campaign_request_id &&
      baseCampaign.state !== 'draft' &&
      isRoleTenantAdmin === false:
      requestConfig = {
        method: 'patch',
        endpoint: `/v4/dash/campaigns/${toUpdate.id}/apply_changes`,
        formdata: {
          properties: filteredDiffData
        },
        successMessage: i18n.t(`${TCampaignCreatePage}.CampaignIsSubmittedForApproval`)
      };
      break;
    default:
      requestConfig = {
        method: 'put',
        endpoint: `/v4/dash/campaigns/${currentFormData.id}`,
        formdata: currentFormData,
        successMessage: i18n.t(`${TCampaignCreatePage}.CampaignUpdated`)
      };
      break;
  }
  return requestConfig;
};
export const prepareCampaignData = (
  data: any,
  isImageFeatureEnabled = false,
  stripCustomFilterRows = true,
  isMultipleRaffleTicketsEnabled = false
) => {
  const sanitizedData = getCampaignPreparedToBeSent(
    data,
    stripCustomFilterRows,
    isMultipleRaffleTicketsEnabled
  );
  fixDateWithTimezoneParam(sanitizedData, 'start_date', 'start_time');
  fixDateWithTimezoneParam(sanitizedData, 'end_date', 'end_time');

  // Convert expiry_fixed_date in outcomes using campaign timezone
  const timezoneSource = sanitizedData.start_time || sanitizedData.end_time;
  if (timezoneSource && Array.isArray(sanitizedData.outcomes)) {
    const tzMatch = timezoneSource.match(/([+-][0-9]{2}:[0-9]{2})$/);
    const tzSuffix = tzMatch ? tzMatch[1] : '';
    const convertExpiryDate = (item: any) => {
      if (item?.expiry_fixed_date && !item.expiry_fixed_date.endsWith('Z')) {
        const dateStr = item.expiry_fixed_date.substring(0, 10);
        const timeStr = item.expiry_fixed_date.substring(11, 16);
        item.expiry_fixed_date = fixDateWithTimezone(dateStr, timeStr + tzSuffix);
      }
      if (Array.isArray(item?.outcomes)) {
        item.outcomes.forEach(convertExpiryDate);
      }
    };
    sanitizedData.outcomes.forEach(convertExpiryDate);
  }

  return sanitizeCampaign(sanitizedData, isImageFeatureEnabled);
};

const transformBubblePopData = (data: any) => {
  const { outcomes, ...rest } = data;
  const validOutcomes = Array.isArray(outcomes) ? outcomes : [];

  const outcomePerScore = (outcomes || []).map(outcome => ({
    score: outcome.score,
    outcomes: [outcome]
  }));

  // bubble_configuration now lives in display_properties, not game_settings
  const { bubble_configuration, bubble_spawn_rate, ...cleanGameSettings } =
    rest.game_settings || {};

  const updatedGameSettings = {
    ...cleanGameSettings,
    outcome_per_score: outcomePerScore
  };

  return {
    ...rest,
    outcomes: validOutcomes,
    game_settings: updatedGameSettings
  };
};

const CLAW_ICON_VALUE_KEYS = ['image_url', 'image_id', 'filename', 'section', 'file'];

const getClawGameSettings = (gameSettings: any): { arm_accuracy?: number; prize_mode?: string } => {
  const source =
    gameSettings && typeof gameSettings === 'object' && !Array.isArray(gameSettings)
      ? gameSettings
      : {};
  const accuracy = Number(source.arm_accuracy);
  const hasAccuracy =
    source.arm_accuracy !== null &&
    source.arm_accuracy !== undefined &&
    source.arm_accuracy !== '' &&
    Number.isFinite(accuracy);

  return {
    ...(hasAccuracy ? { arm_accuracy: accuracy } : {}),
    ...(source.prize_mode ? { prize_mode: source.prize_mode } : {})
  };
};

const CLAW_IMAGE_KEYS = [
  'background_image',
  'prize_box_image',
  'blindbox_prize_image',
  'dummy_prize_image'
];

/**
 * `getStatic` replicates every placeholder static into each `display_properties_<locale>`, so an
 * image the maker never picked travels as an empty shell. The backend requires `image_url` on any
 * image it is handed, and rejects the whole update with 4221 — so the empty ones come off the wire
 * rather than going out blank.
 */
const hasImageSource = (image: any) =>
  Boolean(image?.value?.image_url || image?.value?.image_id || image?.value?.file?.length);

const stripEmptyClawImages = (properties: any) => {
  if (!properties || typeof properties !== 'object') {
    return properties;
  }

  CLAW_IMAGE_KEYS.forEach(key => {
    if (key in properties && !hasImageSource(properties[key])) {
      delete properties[key];
    }
  });

  return properties;
};

const transformClawData = (data: any) => {
  const { outcomes, display_properties, game_settings, ...rest } = data;

  Object.keys(rest)
    .filter(key => key.startsWith('display_properties_'))
    .forEach(key => stripEmptyClawImages(rest[key]));

  const gameSettings = getClawGameSettings(game_settings);
  const clawOutcomes = Array.isArray(outcomes) ? outcomes : null;
  const isFixedMode = gameSettings.prize_mode === 'fixed';

  const prizeIcons = isFixedMode
    ? (clawOutcomes || [])
        .filter(outcome => isAssignedSlot(outcome?.slot) && outcome?.icon?.value)
        .map(outcome => ({
          type: 'image',
          slot: outcome.slot,
          value: _pick(outcome.icon.value, CLAW_ICON_VALUE_KEYS)
        }))
    : [];

  const baseDisplayProperties =
    display_properties && typeof display_properties === 'object' ? { ...display_properties } : null;
  // The icons are authored on the outcome rows, so they still have to reach the wire on a campaign
  // whose display fields came back from the API inside display_properties_<locale> only.
  const displayProperties = baseDisplayProperties ?? (prizeIcons.length > 0 ? {} : null);

  if (displayProperties) {
    stripEmptyClawImages(displayProperties);

    if (prizeIcons.length > 0) {
      displayProperties.prize_icons = prizeIcons;
    } else {
      delete displayProperties.prize_icons;
    }
  }

  // `slot` only models the fixed-mode pairing; random mode recreates every outcome without one.
  const stripRowPairing = ({ icon, slot, ...outcome }: any) =>
    isFixedMode && slot !== undefined ? { ...outcome, slot } : outcome;

  return {
    ...rest,
    ...(clawOutcomes ? { outcomes: clawOutcomes.map(stripRowPairing) } : {}),
    ...(displayProperties ? { display_properties: displayProperties } : {}),
    ...(_isEmpty(gameSettings) ? {} : { game_settings: gameSettings })
  };
};

const RAFFLE_TICKET_KEYS = ['earn_type', 'outcome_value', 'outcome_base', 'max_outcome_value'];

// `stripCustomFilterRows` must stay false when preparing the maker-checker diff baselines
// (baseCampaign / initialFormValues): stripping an incomplete custom row from the baseline
// hides its removal from the diff, so the bad filter would silently persist (PLA-7230).
export const getCampaignPreparedToBeSent = (
  formValues,
  stripCustomFilterRows = true,
  isMultipleRaffleTicketsEnabled = false
) => {
  let data = _cloneDeep(formValues);

  data.duration_ends_at = data.end_date ?? null;

  // Remove duplicate-flow and UI-only fields before create/update requests.
  delete data.duplicateID;
  delete data.end_date_backup;
  delete data.hideDurationEndDate;
  delete data.hideDurationEndDateInit;

  if (data.campaign_type === 'quiz' || data.campaign_type === 'survey') {
    data.game_type = data.campaign_type;
    data.campaign_type = 'game';
  }

  // Quiz/Survey persist every language nested inside a single display_properties
  // object (remapped to display_properties_en below); they have no legitimate
  // per-locale display_properties_<locale> sibling. Such siblings only ever appear
  // as leaked state from switching campaign type out of Progress/Streak/Quest, which
  // seed every locale with header/body/milestones_success_image/outcome_settings —
  // all rejected by the Quiz/Survey BE schema (4222). Drop every non-EN sibling so
  // the BE never sees them; real translations live nested inside _en. — PLA-8758
  if (data.game_type === 'quiz' || data.game_type === 'survey') {
    Object.keys(data).forEach(key => {
      if (key.startsWith('display_properties_') && key !== 'display_properties_en') {
        delete data[key];
      }
    });
  }

  if (data.campaign_type === 'streak') {
    if (data.streak_campaign_mechanics?.streak_mode === 'progressive') {
      if (data.streak_campaign_mechanics?.streak_type === 'cumulative_spend') {
        data.streak_intervals =
          data.milestones?.map((milestone, index) => ({
            id: milestone?.id,
            position: index + 1,
            target_spend: Number(milestone.target_spend),
            streak_period: data.temp.streak_period,
            streak_unit: Number(milestone.streak_unit),
            outcomes: milestone.outcomes
          })) || [];
      } else {
        data.streak_intervals =
          data.milestones?.map((milestone, index) => ({
            id: milestone?.id,
            position: index + 1,
            event_count: Number(milestone.event_count),
            streak_period: data.temp.streak_period,
            streak_unit: Number(milestone.streak_unit),
            outcomes: milestone.outcomes
          })) || [];
      }
    } else {
      if (data.streak_campaign_mechanics?.streak_type === 'cumulative_spend') {
        data.streak_intervals = [
          {
            id: data.temp?.interval_id,
            position: 1,
            target_spend: Number(data.temp.target_spend),
            streak_period: data.temp.streak_period,
            streak_unit: Number(data.temp.streak_unit),
            outcomes: data.outcomes || []
          }
        ];
      } else {
        data.streak_intervals = [
          {
            id: data.temp?.interval_id,
            position: 1,
            event_count: Number(data.temp.event_count),
            streak_period: data.temp.streak_period,
            streak_unit: Number(data.temp.streak_unit),
            outcomes: data.outcomes || []
          }
        ];
      }
    }
    if (data.id) {
      data.rules = data?.rules?.map(rule => {
        return {
          ...rule,
          event_params: {
            ...rule.event_params,
            campaign_id: Number(data.id)
          }
        };
      });
    }

    data.outcomes = [];
    data.milestones = [];
    delete data.temp;
  }

  if (data.campaign_type !== 'streak') {
    delete data.streak_campaign_mechanics;
  }

  if (data.campaign_type === 'instant_outcome') {
    const rules = data.rules || [];
    const ruleNameToFind = 'Push Notification Rule for Instant Outcome Transaction Issue';

    const existingRule = rules.find(rule => rule?.name === ruleNameToFind) || {};

    const remainingRules = rules.filter(rule => rule?.name !== ruleNameToFind);

    if (data.pushNotificationRule?.send_notification) {
      const newRule = {
        name: ruleNameToFind,
        rule_type: 'campaign_rule',
        event_name: 'campaign_completed',
        event_params: { campaign_id: Number(data.id) },
        ...existingRule,
        id: data.duplicateID ? null : data.pushNotificationRule?.ruleId,
        send_notification: data.pushNotificationRule?.send_notification || false,
        outcome_options: {
          selected_media_type_id:
            data.pushNotificationRule?.outcome_options?.selected_media_type_id || null,
          custom_fields: data.pushNotificationRule?.outcome_options?.custom_fields || {}
        }
      };
      delete newRule['send_notification_on_auto_redeem'];
      if (data?.pushNotificationRule?.ruleId)
        newRule.id = data.duplicateID ? null : data.pushNotificationRule.ruleId;
      remainingRules.push(newRule);
    }

    delete data.pushNotificationRule;
    data.rules = remainingRules;
  }

  // Outer-outcome `probability` is consumed by the BE ONLY as input to the
  // RandomizeOutcomesService lottery: fetch_outcomes_for_issuance.rb runs the
  // randomizer whenever ANY outcome has a non-null probability, otherwise returns
  // the full set. For CYR ("Customers choose their preferred prize",
  // outcome_choices_config.enabled) the user picks the reward, so a lottery must
  // never run: with probability present the BE randomly disables outcome_choices,
  // and the redeem re-rolls the lottery — 422-ing even the choice that was shown
  // enabled (PLA-8790). Win Odds is instead a campaign-level value on
  // outcome_choices_config.probability, which the BE rolls once per play
  // (PLA-9059, perx-api #12634) — it never derives from these weights. Strip
  // probability from every outcome when CYR is enabled — including bubble_pop,
  // which already stripped.
  //
  // Non-game campaign types (Quest / Stamp / Streak / Progress / Instant Reward /
  // Raffle / Rule Group) expose no Win Probability UI, but PrizeSet still seeds
  // probability: 0 on the no_outcome row (PLA-8214), so strip there too.
  // game-without-CYR (spin/quiz/survey lucky-draw): probability is user-set and
  // legitimately drives the randomizer, so leave it as-is.
  const shouldStripOuterProbability =
    data.campaign_type !== 'game' || data.outcome_choices_config?.enabled;
  if (shouldStripOuterProbability && Array.isArray(data.outcomes)) {
    data.outcomes = data.outcomes.map(outcome => {
      const { probability, ...rest } = outcome;
      return rest;
    });
  }

  if (data.outcome_choices_config) {
    data.outcome_choices_config = prepareWinProbabilityForSubmit(
      data.outcome_choices_config,
      data.outcomes
    );
  }

  // Backend rejects probability <= 0 on top-level milestone/interval outcomes
  // (PLA-8190). The UI may seed probability: 0 on new outcomes; only
  // randomized_outcome_set children (nested in outcome.outcomes) need the
  // probability field.
  const stripProbabilityFromOutcomes = (outcomes: any[]) =>
    outcomes.map(outcome => {
      if (!outcome || typeof outcome !== 'object') return outcome;
      const { probability, ...rest } = outcome;
      return rest;
    });

  if (Array.isArray(data.milestones)) {
    data.milestones = data.milestones.map(milestone =>
      milestone && Array.isArray(milestone.outcomes)
        ? { ...milestone, outcomes: stripProbabilityFromOutcomes(milestone.outcomes) }
        : milestone
    );
  }

  if (Array.isArray(data.streak_intervals)) {
    data.streak_intervals = data.streak_intervals.map(interval =>
      interval && Array.isArray(interval.outcomes)
        ? { ...interval, outcomes: stripProbabilityFromOutcomes(interval.outcomes) }
        : interval
    );
  }

  if (data.game_type === 'bubble_pop') {
    data = transformBubblePopData(data);
  }

  // Remove has_reward from wedges for Spin the Wheel V2
  if (
    data.game_type === 'spin_the_wheel' &&
    Object.keys(data).some(
      key => key.startsWith('display_properties') && data[key]?.outcome_per_wedge
    )
  ) {
    Object.keys(data).forEach(key => {
      if (key.startsWith('display_properties') && data[key]?.wedges) {
        data[key].wedges = data[key].wedges.map(({ has_reward, ...rest }) => rest);
      }
    });
  }

  if (data.game_type === 'claw_machine') {
    data = transformClawData(data);
  }

  if (!data['include_audience_file']) {
    delete data['include_audience_file'];
  }
  if (!data['exclude_audience_file']) {
    delete data['exclude_audience_file'];
  }

  if (data.rules?.length) {
    data.rules = data.rules.map(item => {
      const normalizedRule = {
        ...item,
        begins_at: data.start_date ?? item.begins_at ?? null,
        ends_at: data.end_date ?? null
      };

      if (normalizedRule?.labels?.length > 0) {
        normalizedRule.labels = normalizedRule.labels.map(label => label?.id);
      }
      if (normalizedRule.conditions === undefined) {
        normalizedRule.conditions = [];
      }

      // Untouched rules are re-sent as read; the BE rejects an empty quantity once the flag is on,
      // and a request submitted with the flag off must not carry ticket keys into a later approval.
      if (data.campaign_type === 'raffle') {
        if (isMultipleRaffleTicketsEnabled) {
          normalizedRule.earn_type = normalizedRule.earn_type ?? 'fixed';
          normalizedRule.outcome_value = normalizedRule.outcome_value ?? 1;
        } else {
          RAFFLE_TICKET_KEYS.forEach(key => delete normalizedRule[key]);
        }
      }

      return normalizedRule;
    });
  }

  if (data.game_type === 'quiz') {
    //
    // PROD-1741: position must always track the CURRENT question count. This used to
    // return any pre-existing position untouched, so adding questions to an already
    // saved quiz left the threshold behind (a 3-question quiz grown to 5 kept
    // position 3). The BE closest-below ladder (Game::Quiz#calculate_position) then
    // issued the outcome to anyone scoring at or above the stale position, i.e. 3 of 5
    // correct still won.
    //
    // The old guard was `outcome.position && outcome.position > -1`, which never
    // preserved the VS-4684 position = 0 case anyway — 0 is falsy in JS, so it was
    // already overwritten on every save. Behaviour for 0 is therefore unchanged.
    data.outcomes = data.outcomes.map(outcome => ({
      ...outcome,
      position: data.display_properties.questions.length
    }));
  }

  if (data.game_type === 'quiz' || data.game_type === 'survey') {
    // remap display_properties to display_properties_en
    data.display_properties_en = { ...data.display_properties };
    delete data.display_properties;
  }

  if (data.campaign_type === 'raffle') {
    // TODO: currently backend throws an error on request if there are any extra parameters sent.
    // Therefore, strip out frontend-only variables.
    // In the future, it probably can be removed.
    data?.raffle_grand_prizes?.forEach(prize => {
      delete prize._id;

      prize?.outcomes?.forEach(outcome => {
        delete outcome._id;
      });
    });

    const raffleGrandPrizesCount = getRaffleGrandPrizePositionsCount(
      data.raffle_grand_prizes || []
    );
    const shouldRemovePrizeLimitToggleValue =
      typeof data.prizes_per_raffle_winner !== 'number' ||
      data.prizes_per_raffle_winner <= 0 ||
      (data.prizes_per_raffle_winner === 1 && raffleGrandPrizesCount < 2);

    if (shouldRemovePrizeLimitToggleValue) {
      delete data.prizes_per_raffle_winner;
    }
  }

  if (data.campaign_type !== 'raffle' && _has(data, 'prizes_per_raffle_winner')) {
    delete data.prizes_per_raffle_winner;
  }

  if (data.filters) {
    data.filters = stripCustomFilterRows
      ? sanitizeFilters(data.filters)
      : _pickBy(data.filters, doesFilterHaveValue);
  }

  if (Array.isArray(data.outcomes) && data.outcomes.length !== 0) {
    const updatedOutcomes = data.outcomes.map(option => {
      if (option?.campaign_options?.outcome_properties) {
        return {
          ...option,
          campaign_options: {
            ...option.campaign_options,
            outcome_properties: _pickBy(
              option.campaign_options.outcome_properties,
              (_value, key) => key === 'gl_account_id'
            )
          }
        };
      }
      return option;
    });

    data.outcomes = updatedOutcomes;
  }

  // Extract rule_details from variable points outcomes and add to rules array.
  // The backend processes outcomes (campaign_modules) and rules (transaction_rules)
  // separately — variable points config must be sent in the rules array.
  // Scan top-level outcomes and nested outcomes (prize sets, randomized sets, etc.)
  if (Array.isArray(data.outcomes)) {
    const extractRuleDetails = (outcome: any) => {
      if (outcome.rule_details) {
        if (!Array.isArray(data.rules)) {
          data.rules = [];
        }
        const newRule = {
          ...outcome.rule_details,
          event_name: outcome.rule_details.event_name || 'transactions_created'
        };
        const existingIndex = data.rules.findIndex(
          r =>
            r.event_name === newRule.event_name &&
            r.rule_type === 'campaign_rule' &&
            !r.send_notification
        );
        if (existingIndex >= 0) {
          data.rules[existingIndex] = { ...data.rules[existingIndex], ...newRule };
        } else {
          data.rules.push(newRule);
        }
        delete outcome.rule_details;
      }
      // Check nested outcomes (prize sets, randomized outcome sets)
      if (Array.isArray(outcome.outcomes)) {
        outcome.outcomes.forEach(extractRuleDetails);
      }
    };
    data.outcomes.forEach(extractRuleDetails);
  }

  if (data.temp) {
    delete data.temp;
  }

  // Tier-based invite: translate internal form shape to the API contract wire
  // shape (top-level tier_based_invite + tiers[], reshaped campaign-level
  // outcomes). No-op for fixed-mode and non-invite campaigns.
  data = tierFormToPayload(data);

  return data;
};

const updateCodeGenerationMethod = (data: any) => {
  data.code_generation_method = 'multiple_with_user_upload';
};

const handleCampaignDurationMechanics = (data: any) => {
  if (!data.campaign_duration_mechanics && 'campaign_duration_days' in data) {
    delete data.campaign_duration_days;
  }
  delete data.campaign_duration_mechanics;
};

const sanitizeCampaign = (clonedData: any, isImageFeatureEnabled = false) => {
  const data = _cloneDeep(clonedData);
  // remove display_properties from games and only use locale version
  const shouldSanitizeDisplayProperties = isImageFeatureEnabled
    ? data.campaign_type === 'game' && data.game_type !== 'quiz' && data.game_type !== 'survey'
    : (data.campaign_type === 'game' && data.game_type !== 'quiz' && data.game_type !== 'survey') ||
      data.campaign_type === 'stamp' ||
      data.campaign_type === 'quest' ||
      data.campaign_type === 'raffle';

  if (shouldSanitizeDisplayProperties) {
    sanitizeDisplayProperties(data);
  }

  // Check if code generation method needs to be updated
  const shouldUpdateCodeGenerationMethod =
    data && data.code_generation_method === 'multiple' && !data.user_generated;
  if (shouldUpdateCodeGenerationMethod) {
    updateCodeGenerationMethod(data);
  }

  const shouldHandleCampaignDurationMechanics =
    (data.campaign_type === 'quest' || data.campaign_type === 'progress') &&
    'campaign_duration_mechanics' in data;

  if (shouldHandleCampaignDurationMechanics) {
    handleCampaignDurationMechanics(data);
  }
  return data;
};

const sanitizeDisplayProperties = (data: any) => {
  const dataKeys = Object.keys(data);
  const displayPropertiesLocaleKeys = dataKeys.filter(k => k.startsWith('display_properties_'));

  // Clean image_id from display_properties before processing
  if (data.display_properties) {
    data.display_properties = cleanImageIds(data.display_properties);
  }

  // Get static display properties (non-textual, graphical data)
  const displayProperties = getStatic(data.campaign_type, data.game_type, data.display_properties);

  displayPropertiesLocaleKeys.forEach(dpLocale => {
    if (data[dpLocale] && Object.keys(data[dpLocale]).length > 0) {
      // Clean image_id from locale-specific display properties
      data[dpLocale] = cleanImageIds(data[dpLocale]);

      if (data.game_type === 'spin_the_wheel') {
        data[dpLocale].wedges = [];
      }
      // Merge using `customMergeForImages`
      data[dpLocale] = _mergeWith(
        {},
        data[dpLocale],
        displayProperties,
        (objValue, srcValue, key) => {
          if (key === 'wedges' && Array.isArray(srcValue)) {
            const array = Array.isArray(objValue) ? [...objValue, ...srcValue] : [...srcValue];
            const strigifyArray = array.map(val => JSON.stringify(val));
            return Array.from(new Set(strigifyArray)).map(val => JSON.parse(val));
          }
          return customMergeForImages(objValue, srcValue) ?? undefined;
        }
      );
    }
  });

  // Last word on the wire: the merge above re-seeds every locale from the placeholder statics, so an
  // image the maker never picked comes back as an empty shell after any earlier cleaning.
  if (data.game_type === 'claw_machine') {
    displayPropertiesLocaleKeys.forEach(dpLocale => stripEmptyClawImages(data[dpLocale]));
  }

  delete data.display_properties;
};
